/* eslint-disable */
import { ethers, providers } from "ethers";
import { contractJsons, pool } from "ambrosus-node-contracts";
import { headContractAddress } from "ambrosus-node-contracts/config/config";
import {
  abiPoolsWithLimit,
  network,
  transactionGasLimit,
  transactionGasPrice,
} from "config";
import { math, FIXED_POINT, parseFloatToBigNumber, ZERO } from "./numbers";
import { debugLog, formatDate } from "utils/helpers";
import moment from "moment";

export default class StakingWrapper {
  static privateProvider = null;

  static poolsStore = null;

  static privateStaticConstructorPromise = (async () => {
    this.privateProvider = new providers.JsonRpcProvider(
      process.env.REACT_APP_RPC_URL
    );

    const headContract = new ethers.Contract(
      headContractAddress,
      contractJsons.head.abi,
      this.privateProvider
    );
    const contextContract = new ethers.Contract(
      await headContract.context(),
      contractJsons.context.abi,
      this.privateProvider
    );
    const storageCatalogueContr = new ethers.Contract(
      await contextContract.storageCatalogue(),
      contractJsons.storageCatalogue.abi,
      this.privateProvider
    );
    const poolsStoreAddr = await storageCatalogueContr.poolsStore();

    this.poolsStore = new ethers.Contract(
      poolsStoreAddr,
      contractJsons.poolsStore.abi,
      this.privateProvider
    );
    debugLog("StakingWrapper done");
  })();

  static async getPools() {
    const poolsCount = await this.poolsStore.getPoolsCount();
    return poolsCount && poolsCount.gt(ZERO)
      ? this.poolsStore.getPools(0, poolsCount)
      : null;
  }

  static async getPoolsData(provider) {
    const providerOrSigner = provider
      ? provider.getSigner()
      : this.privateProvider;

    await this.privateStaticConstructorPromise;

    const [poolsAddrs] = await Promise.all([this.getPools()]);

    if (!poolsAddrs || !poolsAddrs.length) return [];
    const poolsApi = await fetch(
      network
        ? "https://staking-api.ambrosus.io/pools/v2"
        : "https://staking-api.ambrosus-test.io/pools/v2"
    );
    const response = await poolsApi.json();
    const polsApiData = response.data;

    const poolsDataPromises = poolsAddrs.map(async (poolAddr, index) => {
      const poolContract = new ethers.Contract(
        poolAddr,
        poolAddr === "0x7fecce438b087a83E95C91DC168ad3D150225543"
          ? abiPoolsWithLimit
          : pool.abi,
        providerOrSigner
      );

      const [
        contractName,
        active,
        totalStakeInAMB,
        tokenPriceAMB,
        myStakeInTokens,
        maxUserTotalStake,
        userStake,
      ] = await Promise.all([
        poolContract.name && poolContract.name(),
        poolContract.active && poolContract.active(),
        poolContract.totalStake && poolContract.totalStake(),
        poolContract.getTokenPrice && poolContract.getTokenPrice(),
        poolContract.viewStake && poolContract.viewStake(),
        poolContract.maxUserTotalStake && poolContract.maxUserTotalStake(),
        poolContract.getStake && poolContract.getStake(),
      ]);

      let days = [];
      let day = moment();
      let count = 0;
      while (count <= 10) {
        count++;
        days.push(formatDate(day.valueOf(), true));
        day = day.clone().add(-1, "d");
      }
      const rewardsArr =
        contractName &&
        days.reverse().map((day, indexN) => {
          return {
            timestamp: day,
            reward:
              polsApiData &&
              polsApiData[contractName] &&
              polsApiData[contractName].rewards
                ? polsApiData[contractName].rewards[10 - indexN]
                : null,
          };
        });
      const myStakeInAMB = myStakeInTokens.mul(tokenPriceAMB).div(FIXED_POINT);
      const estAR = math
        .chain(
          polsApiData[contractName] && polsApiData[contractName].apy
            ? polsApiData[contractName].apy
            : "0"
        )
        .divide(100)
        .multiply(myStakeInAMB.toString())
        .divide(FIXED_POINT.toString())
        .round(2)
        .done()
        .toFixed(2);

      return (
        rewardsArr && {
          index,
          contractName,
          address: poolAddr,
          active,
          contract: poolContract,
          totalStakeInAMB,
          tokenPriceAMB,
          myStakeInTokens,
          myStakeInAMB,
          poolAPY:
            polsApiData &&
            polsApiData[contractName] &&
            polsApiData[contractName]?.apy,
          estAR,
          poolRewards: rewardsArr.slice(1, rewardsArr.length - 1),
          // maxPoolTotalStake,
          maxUserTotalStake,
          userStake,
        }
      );
    });
    return Promise.all(poolsDataPromises);
  }

  static async stake(poolInfo, value) {
    const overrides = {
      value: parseFloatToBigNumber(value),
      gasPrice: transactionGasPrice,
      gasLimit: transactionGasLimit,
    };

    const poolContract = poolInfo.contract;
    try {
      overrides.gasLimit = await poolContract.estimateGas.stake(overrides);
      return poolContract.stake(overrides);
    } catch (err) {
      debugLog("stake error", err);
      return null;
    }
  }

  static async unstake(poolInfo, value, fullUnstake = false) {
    const poolContract = poolInfo.contract;
    const [tokenPriceAMB, myStakeInTokens] = await Promise.all([
      poolContract.getTokenPrice(),
      poolContract.viewStake(),
    ]);

    const tokens = fullUnstake
      ? myStakeInTokens
      : parseFloatToBigNumber(value).mul(FIXED_POINT).div(tokenPriceAMB);

    const overrides = {
      gasPrice: transactionGasPrice,
      gasLimit: transactionGasLimit,
    };

    try {
      overrides.gasLimit = await poolContract.estimateGas.unstake(
        tokens,
        overrides
      );
      return poolContract.unstake(tokens, overrides);
    } catch (err) {
      debugLog("unstake error", err);
      return null;
    }
  }
}
